'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

interface Instructor {
    name: string;
    role: string;
    img: string;
    alt: string;
    stat: string;
    statLabel: string;
    color: string;
    mode: string;
    modeColor: string;
}

const instructors: Instructor[] = [
    {
        name: 'Chef Amaka',
        role: 'Culinary Arts',
        img: "https://img.rocket.new/generatedImages/rocket_gen_img_1fa663164-1774368054678.png",
        alt: 'Nigerian female chef teaching cooking class in bright kitchen, professional attire, smiling at camera',
        stat: '3.2k',
        statLabel: 'students',
        color: 'border-orange/30',
        mode: 'Live Slots',
        modeColor: 'text-orange bg-orange/8 border-orange/20'
    },
    {
        name: 'Tunde Okafor',
        role: 'Software Development',
        img: "https://img.rocket.new/generatedImages/rocket_gen_img_1130825cf-1785830975456.png",
        alt: 'Young African male developer teaching coding session at a desk in a bright modern office',
        stat: '5.1k',
        statLabel: 'students',
        color: 'border-primary/30',
        mode: 'Bootcamp',
        modeColor: 'text-primary bg-primary/8 border-primary/20'
    },
    {
        name: 'Ngozi Eze',
        role: 'Professional Hairdressing',
        img: "https://img.rocket.new/generatedImages/rocket_gen_img_17434de44-1772094377721.png",
        alt: 'African female hairstylist teaching hair braiding in a bright modern salon',
        stat: '2.8k',
        statLabel: 'students',
        color: 'border-coral/30',
        mode: 'Live Slots',
        modeColor: 'text-coral bg-coral/8 border-coral/20'
    },
    {
        name: 'Emeka Obi',
        role: 'Digital Marketing',
        img: "https://img.rocket.new/generatedImages/rocket_gen_img_191b7bc0e-1767082357764.png",
        alt: 'African male marketing professional teaching digital strategy in a bright office setting',
        stat: '6.3k',
        statLabel: 'students',
        color: 'border-green/30',
        mode: 'Bootcamp',
        modeColor: 'text-green bg-green/8 border-green/20'
    }];


export default function TeachSection() {
    const sectionRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) entry.target.classList.add('visible');
                });
            },
            { threshold: 0.1 }
        );
        const els = sectionRef.current?.querySelectorAll('.reveal, .reveal-left, .reveal-right');
        els?.forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    return (
        <section id="teach" ref={sectionRef} className="relative py-20 md:py-28 bg-background overflow-hidden">
            {/* Atmospheric */}
            <div className="absolute top-0 left-0 w-[600px] h-[600px] blob-orange opacity-20 -translate-x-1/3 -translate-y-1/3 pointer-events-none" />
            <div className="absolute inset-0 grid-bg pointer-events-none opacity-40" />

            <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8">
                <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                    {/* Left — instructor grid */}
                    <div className="order-2 lg:order-1">
                        <div className="grid grid-cols-2 gap-4">
                            {instructors.map((inst, i) =>
                                <div
                                    key={inst.name}
                                    className="reveal card-hover cursor-pointer"
                                    style={{ transitionDelay: `${i * 100}ms` }}>

                                    <div className={`bg-card rounded-2xl border ${inst.color} overflow-hidden shadow-card group`}>
                                        <div className="aspect-[4/3] overflow-hidden">
                                            <AppImage
                                                src={inst.img}
                                                alt={inst.alt}
                                                width={240}
                                                height={180}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />

                                        </div>
                                        <div className="p-4">
                                            <p className="font-bold text-foreground text-sm">{inst.name}</p>
                                            <p className="text-muted-foreground text-xs font-body mt-0.5">{inst.role}</p>
                                            <div className="flex items-center justify-between mt-2">
                                                <div className="flex items-center gap-1">
                                                    <span className="text-foreground font-bold text-sm">{inst.stat}</span>
                                                    <span className="text-muted-foreground text-xs">{inst.statLabel}</span>
                                                </div>
                                                <span className={`pill-tag border text-xs ${inst.modeColor}`}>{inst.mode}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* More instructor types */}
                        <div className="reveal delay-400 mt-4 bg-card border border-border rounded-2xl p-4 flex flex-wrap gap-2">
                            {['Chef', 'Developer', 'Photographer', 'Hairstylist', 'Designer', 'Musician', 'Fitness Coach', 'Academic Teacher'].map((type) =>
                                <span key={type} className="pill-tag border border-border text-muted-foreground bg-background text-xs hover:border-primary/30 hover:text-primary transition-colors cursor-pointer">
                                    {type}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Right — copy */}
                    <div className="order-1 lg:order-2 flex flex-col gap-6">
                        <div className="reveal">
                            <span className="pill-tag border border-accent/20 text-accent bg-accent/8 text-xs uppercase tracking-wider">
                                For instructors
                            </span>
                        </div>

                        <h2 className="reveal text-section-title font-extrabold text-foreground delay-100">
                            Know something worth sharing? Teach it.
                        </h2>

                        <div className="reveal delay-200 space-y-3 text-muted-foreground font-body text-lg leading-relaxed">
                            <p>
                                Turn your knowledge and skills into learning experiences. No recordings — just real, scheduled sessions with real students.
                            </p>
                            <p>
                                <span className="text-foreground font-semibold">You choose how you teach.</span>{' '}
                                Set your own time slots for students to book, or build a structured bootcamp with a fixed timetable.
                            </p>
                        </div>

                        {/* Two modes */}
                        <div className="reveal delay-300 grid sm:grid-cols-2 gap-4">
                            {/* Mode 1: Live Slots */}
                            <div className="bg-orange/5 border border-orange/20 rounded-2xl p-5">
                                <div className="w-9 h-9 bg-orange/10 rounded-xl flex items-center justify-center text-lg mb-3">📅</div>
                                <h3 className="font-bold text-foreground text-sm mb-1.5">Set Time Slots</h3>
                                <p className="text-muted-foreground text-xs font-body leading-relaxed">
                                    Set your available time slots. Students browse and book sessions directly. You show up, teach, and get paid.
                                </p>
                            </div>
                            {/* Mode 2: Bootcamp */}
                            <div className="bg-primary/5 border border-primary/20 rounded-2xl p-5">
                                <div className="w-9 h-9 bg-primary/10 rounded-xl flex items-center justify-center text-lg mb-3">🚀</div>
                                <h3 className="font-bold text-foreground text-sm mb-1.5">Bootcamps</h3>
                                <p className="text-muted-foreground text-xs font-body leading-relaxed">
                                    Create a structured bootcamp with a fixed timetable. Students pay to enrol and attend your live classes on scheduled days.
                                </p>
                            </div>
                        </div>

                        {/* Feature list */}
                        <ul className="reveal delay-300 space-y-3">
                            {[
                                { icon: '✦', text: 'Set your own schedule and availability', color: 'text-primary' },
                                { icon: '✦', text: 'Students book and pay for your live sessions', color: 'text-orange' },
                                { icon: '✦', text: 'Run bootcamps with a structured timetable', color: 'text-purple' },
                                { icon: '✦', text: 'Track attendance, bookings, and earnings', color: 'text-green' }].
                                map((item) =>
                                    <li key={item.text} className="flex items-start gap-3">
                                        <span className={`${item.color} font-bold mt-0.5 flex-shrink-0`}>{item.icon}</span>
                                        <span className="text-foreground font-medium text-base">{item.text}</span>
                                    </li>
                                )}
                        </ul>

                        {/* CTA */}
                        <div className="reveal delay-400 flex flex-col sm:flex-row gap-3">
                            <Link
                                href="/studio"
                                className="group inline-flex items-center justify-center gap-2.5 px-7 py-3.5 bg-accent text-white rounded-full text-base font-semibold hover:bg-accent/90 shadow-lg hover:-translate-y-0.5 transition-all duration-300"
                                style={{ boxShadow: '0 8px 32px -8px rgba(255, 107, 53, 0.4)' }}>

                                Explore Wisly Studio
                                <svg className="w-4 h-4 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                                    <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </Link>
                            <Link
                                href="/early-access"
                                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-card text-foreground border border-border rounded-full text-base font-semibold hover:border-accent/30 hover:bg-accent/5 hover:-translate-y-0.5 transition-all duration-300">

                                Request Early Access
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </section>);

}